/**
 * CloudDeviceSelectionCard — Cloud 5.0 device & event configuration with 2-column
 * grid of 6 cascading dropdowns + action buttons.
 *
 * Left column:  Device Type → Entity (Area/Resident) → Device
 * Right column: Server (from DB) → Event Category → Event Type (filtered by category)
 *
 * Actions: Create Alarm, Rounds (AIO only), Check-In (AIO only)
 * Simulate Careflow button is in CloudSimulatorCard, not here.
 *
 * All state owned by parent CloudDemoPage. Zero document.getElementById.
 */

const CloudDeviceSelectionCard = ({
    // Device Type
    dataType,
    onDataTypeChange,
    // Entity
    entityOptions,
    entityIndex,
    onEntityChange,
    entityLabel,
    // Device
    deviceOptions,
    deviceIndex,
    onDeviceChange,
    // Server (from DB)
    serverOptions,
    server,
    onServerChange,
    // Event Category
    eventCatOptions,
    eventCatId,
    onEventCatChange,
    // Event Type
    eventTypeOptions,
    eventTypeId,
    onEventTypeChange,
    // Actions
    canSubmit,
    isAioDevice,
    loadingAlarm,
    loadingRounds,
    loadingCheckin,
    onCreateAlarm,
    onRounds,
    onCheckin,
    // Clear-alert sub-section (demo-only, bypasses careflow)
    activeAlarms = [],
    clearEventId = '',
    onClearEventIdChange,
    onClearAlarm,
    clearingEventId,
}) => {
    const canClear = Boolean(clearEventId) && String(clearingEventId ?? '') !== String(clearEventId);
    return (
        <div className="dm-card">
            <h3 className="dm-card__header">Device & Event Configuration</h3>
            <div className="dm-card__body">
                {/* 2-column form grid */}
                <div className="dm-form-grid">
                    {/* Left column: Device Type, Entity, Device */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                        <div>
                            <label htmlFor="dmc-datatype" className="dm-field__label">Device Type</label>
                            <select
                                id="dmc-datatype"
                                className="dm-field__select"
                                value={dataType}
                                onChange={(e) => onDataTypeChange(e.target.value)}
                            >
                                <option value="" disabled>-- Select --</option>
                                <option value="area">Area</option>
                                <option value="resident">Resident</option>
                            </select>
                        </div>

                        <div>
                            <label htmlFor="dmc-entity" className="dm-field__label">{entityLabel || 'Entity'}</label>
                            <select
                                id="dmc-entity"
                                className="dm-field__select"
                                value={entityIndex}
                                onChange={(e) => onEntityChange(e.target.value)}
                                disabled={!dataType}
                            >
                                <option value="" disabled>-- Select --</option>
                                {entityOptions.map((opt) => (
                                    <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                                        {opt.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label htmlFor="dmc-device" className="dm-field__label">Device</label>
                            <select
                                id="dmc-device"
                                className="dm-field__select"
                                value={deviceIndex}
                                onChange={(e) => onDeviceChange(e.target.value)}
                                disabled={!entityIndex}
                            >
                                <option value="" disabled>-- Select --</option>
                                {deviceOptions.map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Right column: Server, Event Category, Event Type */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                        <div>
                            <label htmlFor="dmc-server" className="dm-field__label">Server</label>
                            <select
                                id="dmc-server"
                                className="dm-field__select"
                                value={server}
                                onChange={(e) => onServerChange(e.target.value)}
                            >
                                {serverOptions.length === 0 ? (
                                    <option value="">No available servers</option>
                                ) : serverOptions.map((s) => (
                                    <option key={s.value} value={s.value}>{s.label}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label htmlFor="dmc-event-cat" className="dm-field__label">Event Category</label>
                            <select
                                id="dmc-event-cat"
                                className="dm-field__select"
                                value={eventCatId}
                                onChange={(e) => onEventCatChange(parseInt(e.target.value, 10))}
                            >
                                {eventCatOptions.map((cat) => (
                                    <option key={cat.event_category_id} value={cat.event_category_id}>
                                        {cat.event_category_name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label htmlFor="dmc-event-type" className="dm-field__label">Event Type</label>
                            <select
                                id="dmc-event-type"
                                className="dm-field__select"
                                value={eventTypeId}
                                onChange={(e) => onEventTypeChange(parseInt(e.target.value, 10))}
                            >
                                {eventTypeOptions.map((type) => (
                                    <option key={type.event_type_id} value={type.event_type_id}>
                                        {type.event_type_name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="dm-actions">
                    <button
                        className="dm-btn--danger"
                        onClick={onCreateAlarm}
                        disabled={!canSubmit || loadingAlarm}
                    >
                        {loadingAlarm ? <><span className="dm-spinner" />Creating...</> : 'Create Alarm'}
                    </button>

                    {isAioDevice ? (
                        <>
                            <button
                                className="rp-btn rp-btn--primary"
                                onClick={onRounds}
                                disabled={!canSubmit || loadingRounds}
                            >
                                {loadingRounds ? <><span className="dm-spinner" />Creating...</> : 'Rounds'}
                            </button>
                            <button
                                className="rp-btn rp-btn--primary"
                                onClick={onCheckin}
                                disabled={!canSubmit || loadingCheckin}
                            >
                                {loadingCheckin ? <><span className="dm-spinner" />Creating...</> : 'Check-In'}
                            </button>
                        </>
                    ) : null}
                </div>

                {/* Clear Active Alert — visually segmented demo-only sub-section */}
                {onClearAlarm ? (
                    <div className="dm-clear-section">
                        <div className="dm-clear-section__title">Clear Active Alert</div>
                        <div className="dm-clear-row">
                            <select
                                id="dmc-clear-alert"
                                className="dm-field__select dm-clear-row__select"
                                value={clearEventId}
                                onChange={(e) => onClearEventIdChange?.(e.target.value)}
                                disabled={activeAlarms.length === 0}
                            >
                                <option value="">
                                    {activeAlarms.length === 0 ? '-- No active alerts --' : '-- Select an active alert --'}
                                </option>
                                {activeAlarms.map((a) => {
                                    const who = a.resident_name || a.area_name || '—';
                                    const cat = a.event_category || '';
                                    return (
                                        <option key={a.event_id} value={a.event_id}>
                                            {`[${cat}] ${who} — ${a.device_name || ''}`.trim()}
                                        </option>
                                    );
                                })}
                            </select>
                            <button
                                type="button"
                                className="na-clear-btn dm-clear-row__btn"
                                onClick={() => {
                                    if (clearEventId) onClearAlarm(clearEventId);
                                }}
                                disabled={!canClear}
                            >
                                {clearingEventId ? 'Clearing...' : 'Clear'}
                            </button>
                        </div>
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export default CloudDeviceSelectionCard;
